import { useSessionStore } from '@/stores/session'
import { ref, onMounted, onUnmounted } from 'vue'

/**
 * Composable for managing session heartbeat functionality
 * - Checks session when tab is active
 * - Manages interval based on tab focus state
 * - Continues session checks regardless of user activity
 * - Implements 3-minute cooldown for user activity checks
 */
export function useSessionHeartbeat() {
  const isRunning = ref(false)
  const POLL_INTERVAL = parseInt(import.meta.env.VITE_SESSION_HEARTBEAT_INTERVAL_MINUTES || '5', 10) * 60 * 1000
  const USER_ACTIVITY_COOLDOWN =
    parseInt(import.meta.env.VITE_SESSION_HEARTBEAT_USER_ACTIVITY_COOLDOWN_MINUTES || '3', 10) * 60 * 1000
  const intervalId = ref<NodeJS.Timeout | null>(null)
  const lastUserActivityCheck = ref<number>(Date.now())

  /**
   * Checks the current session status
   */
  const checkSession = async (): Promise<void> => {
    const sessionStore = useSessionStore()
    await sessionStore.ping()
  }

  /**
   * Starts the polling interval for session checks
   */
  const startPolling = (): void => {
    if (isRunning.value) return
    isRunning.value = true
    // Check session immediately when starting
    checkSession()
    intervalId.value = setInterval(() => {
      checkSession()
    }, POLL_INTERVAL)
  }

  /**
   * Stops the polling interval
   */
  const stopPolling = (): void => {
    if (!isRunning.value) return
    if (intervalId.value) clearInterval(intervalId.value)
    intervalId.value = null
    isRunning.value = false
  }

  /**
   * Handles tab visibility changes
   * - Stops interval when tab is hidden
   * - Starts interval when tab becomes visible
   */
  const handleVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      stopPolling()
    } else {
      startPolling()
    }
  }

  /**
   * Handles user activity with 3-minute cooldown
   * Only checks session if more than 3 minutes have passed since last check
   */
  const handleUserActivity = (): void => {
    const now = Date.now()
    const timeSinceLastCheck = now - lastUserActivityCheck.value

    // Only check session if more than 3 minutes have passed since last user activity check
    if (timeSinceLastCheck >= USER_ACTIVITY_COOLDOWN) {
      lastUserActivityCheck.value = now
      if (document.visibilityState === 'visible') {
        checkSession()
      }
    }

    // Ensure polling is running if tab is visible
    if (!isRunning.value && document.visibilityState === 'visible') {
      startPolling()
    }
  }

  onMounted(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('mousemove', handleUserActivity)
    window.addEventListener('keydown', handleUserActivity)
    window.addEventListener('click', handleUserActivity)
    window.addEventListener('scroll', handleUserActivity)

    startPolling()
  })

  onUnmounted(() => {
    stopPolling()

    document.removeEventListener('visibilitychange', handleVisibilityChange)
    window.removeEventListener('mousemove', handleUserActivity)
    window.removeEventListener('keydown', handleUserActivity)
    window.removeEventListener('click', handleUserActivity)
    window.removeEventListener('scroll', handleUserActivity)
  })
}
